import { useCallback } from 'react';
import type { TypeLockedRow } from '@inovua/reactdatagrid-community/types';
import { useLatest } from 'react-use';
import i18next from 'i18next';
import { first, isEmpty } from 'lodash';
import { read, utils } from 'xlsx';

import { useGlobalErrorStore } from '@/components/dialogs';

import { useFieldMatchingStoreActions } from './field-matching.store';

// ----------------------------------------------------------------------

export type ColumnByRowItem = {
  name: string;
  header: React.ReactNode;
  flex?: number;
  minWidth?: number;
};

export type UploadedFieldMatchingRow = Record<string, React.ReactNode>;

type LockedRows = Omit<TypeLockedRow, 'render'> & {
  render: UploadedFieldMatchingRow;
};

export type UploadedFieldMatchingSettings = {
  columns: ColumnByRowItem[];
  lockedRows: LockedRows[];
  dataSource: UploadedFieldMatchingRow[];
};

export type UseFieldMatchingFileUploadConfig = {
  numberOfColumnsFromSettings?: number;
  renderGridOption: (columnNumber: number) => React.ReactNode;
  renderGridHeader: (columnNumber: number) => React.ReactNode;
  onUpload: (uploadedFieldMatchingSettings: UploadedFieldMatchingSettings) => void;
};

// ----------------------------------------------------------------------

const defaultColumnSettings = {
  flex: 1,
  minWidth: 220,
};

// First column with row number
const getRowNumberColumnSettings = () => ({
  minWidth: 120,
  name: 'rowNumber',
  header: i18next.t('field-matching-form.use-field-matching-file-upload.row-number-column-settings.header', {
    ns: 'field-matching',
  }),
});

export const useFieldMatchingFileUpload = (config: UseFieldMatchingFileUploadConfig) => {
  const latestConfig = useLatest(config);

  const { setGlobalError } = useGlobalErrorStore();
  const { setUploadedFileColumnsNumber, setUploadedFileRowsNumber } = useFieldMatchingStoreActions();

  const handleDrop = useCallback(
    (acceptedFiles: Blob[]) => {
      const file = first(acceptedFiles);

      if (!file) {
        return;
      }

      const reader = new FileReader();

      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => console.log('file reading has failed');
      reader.onload = () => {
        try {
          const { renderGridOption, renderGridHeader, onUpload } = latestConfig.current;

          const workBook = read(reader.result);
          const workSheet = workBook.Sheets[workBook.SheetNames[0]];

          const sheetData = utils
            .sheet_to_json<Array<string | undefined>>(workSheet, {
              raw: false,
              rawNumbers: false,
              blankrows: true,
              skipHidden: false,
              header: 1,
            })
            .filter(sheetItem => !isEmpty(sheetItem));

          // ----------------------------------------------------------------------

          // Setting columns for DataGrid
          let columnsSettings: ColumnByRowItem[] = [getRowNumberColumnSettings()];

          // List of column customization options
          const lockedRow: LockedRows = {
            position: 'start',
            render: {},
          };

          let prevRowIndex: number | null = null;

          // Data source for DataGrid
          const dataSource: UploadedFieldMatchingRow[] = [];

          sheetData.forEach((rowData, rowIndex) => {
            if (rowData.length === 0) {
              return;
            }

            const columnsDataByName: UploadedFieldMatchingRow = {
              rowNumber: rowIndex + 1,
            };

            const numberOfColumns = rowData.length;

            const numberOfColumnsWithRowNumberColumn = numberOfColumns + 1;

            for (let columnIndex = 0; columnIndex < numberOfColumnsWithRowNumberColumn; columnIndex++) {
              const columnData = rowData[columnIndex];
              const columnNumber = columnIndex + 1;
              const columnName = `column-${columnNumber}`;

              columnsDataByName[columnName] = columnData || '';

              // Check if sheet row is longer than before
              if (numberOfColumnsWithRowNumberColumn > columnsSettings.length) {
                // If the sheet row has changed, then the columns settings will be reset
                if (prevRowIndex !== rowIndex) {
                  prevRowIndex = rowIndex;
                  columnsSettings = [getRowNumberColumnSettings()];
                }

                // Setting columns for DataGrid
                columnsSettings.push({
                  ...defaultColumnSettings,
                  name: columnName,
                  header: renderGridHeader(columnNumber),
                });

                // List of column customization options
                lockedRow.render[columnName] = renderGridOption(columnNumber);
              }
            }

            dataSource.push(columnsDataByName);
          });

          // ----------------------------------------------------------------------

          setUploadedFileColumnsNumber(columnsSettings.length - 1);
          setUploadedFileRowsNumber(dataSource.length);

          onUpload({ dataSource, lockedRows: [lockedRow], columns: columnsSettings });
        } catch (error) {
          setGlobalError(error);
        }
      };

      // ----------------------------------------------------------------------

      reader.readAsArrayBuffer(file);
    },
    /* eslint-disable-next-line */
    [latestConfig, setGlobalError],
  );

  return {
    parseUploadedFile: handleDrop,
  };
};
